/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

import { useState, FormEvent, CSSProperties } from 'react';

import { isBoolean } from 'lodash-es';

import AKAvatar from '@atlaskit/avatar';
import { Checkbox } from '@atlaskit/checkbox';
import {
  Field,
  ErrorMessage,
  CheckboxField as AKCheckboxField,
  HelperMessage,
} from '@atlaskit/form';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import { Inline } from '@atlaskit/primitives';
import AKSelect, { AsyncSelect, OptionType } from '@atlaskit/select';
import TextArea from '@atlaskit/textarea';
import TextFieldAK from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';

import { FakeLabel } from 'src/styles';

import {
  LabelContainer,
  FormatOptionLabelStyled,
  FormatOptionLabelContainerStyled,
  TextAreaFieldContainer,
  OptionLabelContainer,
} from './form-components.style';

type NullableString = string | null;

type TextFieldProps = {
  defaultValue: NullableString | number;
  label: string;
  name: string;
  placeholder?: string;
  width?: string | number;
  isRequired?: boolean;
  isDisabled?: boolean;
  testId?: string;
  validate?: (value: string) => string | undefined;
  transform?: (value: FormEvent<HTMLInputElement>) => string | undefined;
  onKeyPress?: (value: any) => void;
  style?: CSSProperties;
  type?: string;
  min?: number | string;
  max?: number | string;
};

type SelectOptions =
  | OptionType[]
  | Array<{ label: string; options: OptionType[] }>;

type SelectFieldProps = {
  defaultFieldValue?: NullableString | number;
  isClearable?: boolean;
  isLoading?: boolean;
  label: string;
  name: string;
  placeholder?: string;
  isRequired?: boolean;
  options: SelectOptions;
  getOptionByValue?: (
    options: SelectOptions,
    currentFieldValue?: NullableString | number
  ) => OptionType | null;
  onChange?: (option: OptionType | null) => void;
  isDisabled?: boolean;
  isSearchable?: boolean;
  validate?: (value: string) => string | undefined;
  styles?: {};
  components?: {};
  isOptionDisabled?: (option: OptionType) => boolean;
  maxMenuHeight?: number;
};

type AsyncSelectFieldProps = {
  defaultValue?: OptionType | null;
  label?: string;
  cacheOptions?: boolean;
  name: string;
  isRequired?: boolean;
  defaultOptions?: boolean;
  loadOptions: (
    inputValue: string,
    callback: (options: OptionType[]) => void
  ) => void;
  onChange?: (option: OptionType) => void;
  isDisabled?: boolean;
  isClearable?: boolean;
  placeholder?: string;
  styles?: {};
  components?: {};
  isMulti?: boolean;
  isSearchable?: boolean;
};

type CheckboxFieldProps = {
  defaultValue: boolean;
  fieldLabel?: string;
  isDisabled?: boolean;
  name: string;
  onChange?: () => void;
  isChecked?: boolean;
  checkboxDescription?: string;
  checkboxLabel?: string;
  testId?: string;
};

type ToggleFieldProps = {
  defaultChecked?: boolean;
  fieldLabel?: string;
  toggleLabel?: string;
  id: string;
  testId?: string;
  name: string;
  onChange?: any;
  isChecked: boolean;
};

type Error = { message: string; code: string };

export const renderErrors = (errors: Error[] | string) => {
  if (typeof errors === 'string') {
    return (
      <div tabIndex={0}>
        <ErrorMessage>{errors}</ErrorMessage>
      </div>
    );
  }
  return errors.map(({ message }: Error, index) => (
    <div key={index} tabIndex={0}>
      <ErrorMessage>{message}</ErrorMessage>
    </div>
  ));
};

export const TextField = ({
  defaultValue,
  label,
  name,
  isRequired,
  isDisabled,
  validate,
  width,
  testId,
  transform,
  placeholder,
  style,
  type,
  min,
  max,
  onKeyPress,
}: TextFieldProps) => (
  <Field
    name={name}
    defaultValue={defaultValue ?? ''}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    validate={validate}
    transform={transform}
  >
    {({ fieldProps, error, valid, meta: { dirtySinceLastSubmit } }) => (
      <>
        <TextFieldAK
          {...fieldProps}
          min={min}
          max={max}
          width={width}
          testId={testId}
          placeholder={placeholder}
          style={style}
          type={type}
          onKeyPress={onKeyPress}
        />
        {(!valid || !dirtySinceLastSubmit) &&
          error &&
          renderErrors(error as unknown as Error[])}
      </>
    )}
  </Field>
);

export const TextAreaField = ({
  defaultValue,
  label,
  name,
  isRequired,
  validate,
  testId,
}: TextFieldProps) => (
  <Field
    name={name}
    defaultValue={defaultValue || ''}
    label={label}
    isRequired={isRequired}
    validate={validate}
  >
    {({ fieldProps, error, valid, meta: { dirtySinceLastSubmit } }: any) => (
      <TextAreaFieldContainer>
        <>
          <TextArea
            {...fieldProps}
            maxHeight={150}
            resize="vertical"
            minimumRows={3}
            testId={testId}
          />
          {(!valid || !dirtySinceLastSubmit) &&
            error &&
            renderErrors(error as unknown as Error[])}
        </>
      </TextAreaFieldContainer>
    )}
  </Field>
);

const selectStyles = {
  container: (styles: { [key: string]: string }) => ({
    ...styles,
    maxWidth: '320px',
  }),
  singleValue: (styles: { [key: string]: string }) => ({
    ...styles,
    width: '100%',
  }),
};

export const AsyncSelectField = ({
  defaultValue,
  loadOptions,
  cacheOptions,
  defaultOptions,
  isRequired,
  label,
  name,
  onChange,
  isDisabled,
  isClearable,
  placeholder,
  styles,
  components,
  isMulti,
  isSearchable,
}: AsyncSelectFieldProps) => {
  const [selectValue, setSelectValue] = useState(defaultValue || null);

  const formatOptionLabel = (option: OptionType) => {
    if (option.icon) {
      return (
        <FormatOptionLabelContainerStyled>
          <FormatOptionLabelStyled>
            <AKAvatar size="xsmall" appearance="square" src={option.icon} />
            <OptionLabelContainer>{option.label}</OptionLabelContainer>
          </FormatOptionLabelStyled>
          {option.isPrivate && <LockLockedIcon spacing="spacious" label="" />}
        </FormatOptionLabelContainerStyled>
      );
    }

    return (
      <FormatOptionLabelContainerStyled>
        <span>{option.label}</span>
        {option.isPrivate && <LockLockedIcon spacing="spacious" label="" />}
      </FormatOptionLabelContainerStyled>
    );
  };

  return (
    <Field
      name={name}
      defaultValue={defaultValue?.value || null}
      label={label}
      isRequired={isRequired}
      isDisabled={isDisabled}
    >
      {({
        fieldProps: {
          value: fieldValue,
          onChange: onFieldChange,
          ...fieldProps
        },
        error,
        valid,
        meta: { dirtySinceLastSubmit },
      }) => (
        <>
          <AsyncSelect
            {...fieldProps}
            placeholder={placeholder}
            validationState={error ? 'error' : 'default'}
            styles={styles || selectStyles}
            value={
              fieldValue === defaultValue?.value ? defaultValue : selectValue
            }
            onChange={(value: OptionType) => {
              setSelectValue(value);
              if (onChange) {
                onChange(value);
              }
              onFieldChange(value.value);
            }}
            loadOptions={loadOptions}
            defaultOptions={defaultOptions}
            formatOptionLabel={formatOptionLabel}
            isClearable={isClearable}
            components={components}
            isMulti={isMulti}
            isSearchable={isSearchable}
            cacheOptions={cacheOptions}
          />
          {(!valid || !dirtySinceLastSubmit) &&
            error &&
            renderErrors(error as unknown as Error[])}
        </>
      )}
    </Field>
  );
};

export const CheckboxField = ({
  defaultValue,
  isDisabled,
  fieldLabel,
  checkboxLabel,
  name,
  onChange,
  isChecked,
  checkboxDescription,
  testId,
}: CheckboxFieldProps) => (
  <AKCheckboxField
    name={name}
    label={fieldLabel}
    defaultIsChecked={defaultValue}
    isDisabled={isDisabled}
  >
    {({
      fieldProps: { onChange: onFieldChange, ...fieldProps },
      error,
      valid,
      meta: { dirtySinceLastSubmit },
    }) => (
      <>
        <Checkbox
          {...fieldProps}
          testId={testId}
          label={checkboxLabel}
          isChecked={isBoolean(isChecked) ? isChecked : fieldProps.isChecked}
          onChange={e => {
            onFieldChange(e);
            if (typeof onChange === 'function') {
              onChange();
            }
          }}
        />
        {checkboxDescription && (
          <HelperMessage>{checkboxDescription}</HelperMessage>
        )}
        {(!valid || !dirtySinceLastSubmit) &&
          error &&
          renderErrors(error as unknown as Error[])}
      </>
    )}
  </AKCheckboxField>
);

export const ToggleField = ({
  defaultChecked,
  fieldLabel,
  id,
  testId,
  name,
  onChange,
  isChecked,
  toggleLabel,
}: ToggleFieldProps) => (
  <Field name={name} label={fieldLabel}>
    {({
      fieldProps: { onChange: onFieldChange, ...fieldProps },
      error,
      valid,
      meta: { dirtySinceLastSubmit },
    }) => (
      <Inline alignBlock="center">
        <Toggle
          {...fieldProps}
          id={id}
          testId={testId}
          defaultChecked={defaultChecked}
          isChecked={isChecked}
          onChange={(event: any) => {
            onFieldChange(event.target.value || null);
            if (typeof onChange === 'function') {
              onChange(event.target.value);
            }
          }}
        />
        <label htmlFor={id}>{toggleLabel}</label>
        {(!valid || !dirtySinceLastSubmit) &&
          error &&
          renderErrors(error as unknown as Error[])}
      </Inline>
    )}
  </Field>
);

const defaultGetOptionByValue = (
  options: OptionType[],
  currentValue?: string | null
): OptionType | null =>
  options.find((option: OptionType) => option.value === currentValue) || null;

export const SelectField = ({
  defaultFieldValue,
  options = [],
  isRequired,
  label,
  name,
  placeholder,
  isClearable,
  isLoading,
  getOptionByValue = defaultGetOptionByValue,
  onChange,
  isDisabled,
  isSearchable,
  validate,
  styles,
  components,
  isOptionDisabled,
  maxMenuHeight,
}: SelectFieldProps) => (
  <Field
    name={name}
    // Select wants null as default empty value
    defaultValue={defaultFieldValue || null}
    label={label}
    isRequired={isRequired}
    isDisabled={isDisabled}
    validate={validate}
  >
    {({
      fieldProps: { value: fieldValue, onChange: onFieldChange, ...fieldProps },
      error,
      valid,
      meta: { dirtySinceLastSubmit },
    }) => (
      <>
        <AKSelect
          {...fieldProps}
          styles={styles || selectStyles}
          placeholder={placeholder}
          isSearchable={isSearchable}
          options={options}
          value={getOptionByValue(options, fieldValue)}
          validationState={error ? 'error' : 'default'}
          isClearable={isClearable}
          isLoading={isLoading}
          components={components}
          maxMenuHeight={maxMenuHeight}
          onChange={(value: OptionType | null) => {
            onFieldChange((value?.value as string) || null);
            if (typeof onChange === 'function') {
              onChange(value);
            }
          }}
          isOptionDisabled={isOptionDisabled}
        />
        {(!valid || !dirtySinceLastSubmit) &&
          error &&
          renderErrors(error as unknown as Error[])}
      </>
    )}
  </Field>
);

export const CustomLabel = ({
  label,
  children,
}: {
  label: string;
  children: any;
}): JSX.Element => (
  <LabelContainer>
    <dt>
      <FakeLabel>{label}</FakeLabel>
    </dt>
    <dd>{children}</dd>
  </LabelContainer>
);
